import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "../../components/Snackbar/useSnackbar";
import {
  baseUserTodosSummaryQueryKey,
  getOrgTasksQueryKey,
  getTaskProgramDetailQueryKey,
  getTaskProgramsQueryKey,
} from "../../config";
import {
  AddOccurrenceTraineeData,
  addTaskProgramOccurrenceTrainee,
  bulkAssignTaskPrograms,
  BulkAssignTaskProgramsData,
  createTaskProgram,
  CreateTaskProgramData,
  deleteTaskProgram,
  deleteTaskProgramOccurrence,
  deleteTaskProgramOccurrenceTask,
  getTaskProgramDetail,
  getTaskPrograms,
  TaskProgram,
  TaskProgramDetail,
  TrainingScope,
  triggerTaskProgramForRevision,
  updateTaskProgram,
  UpdateTaskProgramData,
} from "../../services";

export const useGetTaskPrograms = (
  orgId: string,
  scope: TrainingScope = "mine",
  options: { enabled?: boolean } = {}
) => {
  return useQuery<TaskProgram[]>({
    queryKey: getTaskProgramsQueryKey(orgId, scope),
    queryFn: async () => {
      const { data } = await getTaskPrograms(orgId, scope);
      return data;
    },
    enabled: !!orgId && (options.enabled ?? true),
  });
};

export const useGetTaskProgramDetail = (
  orgId: string,
  programId: string,
  options: { enabled?: boolean } = {}
) => {
  return useQuery<TaskProgramDetail>({
    queryKey: getTaskProgramDetailQueryKey(orgId, programId),
    queryFn: async () => {
      const { data } = await getTaskProgramDetail(orgId, programId);
      return data;
    },
    enabled: !!orgId && !!programId && (options.enabled ?? true),
  });
};

export const invalidatePrograms = (
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string,
  programId?: string
) => {
  queryClient.invalidateQueries({
    queryKey: getTaskProgramsQueryKey(orgId, "mine"),
  });
  queryClient.invalidateQueries({
    queryKey: getTaskProgramsQueryKey(orgId, "all"),
  });
  if (programId) {
    queryClient.invalidateQueries({
      queryKey: getTaskProgramDetailQueryKey(orgId, programId),
    });
  }
};

export const invalidateProgramAndTasks = (
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string,
  programId?: string
) => {
  invalidatePrograms(queryClient, orgId, programId);
  queryClient.invalidateQueries({
    queryKey: getOrgTasksQueryKey(orgId, "all"),
  });
  queryClient.invalidateQueries({
    queryKey: getOrgTasksQueryKey(orgId, "mine"),
  });
  queryClient.invalidateQueries({ queryKey: baseUserTodosSummaryQueryKey });
};

export const useCreateTaskProgram = ({ orgId }: { orgId: string }) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (data: CreateTaskProgramData) => {
      const res = await createTaskProgram(orgId, data);
      return res.data;
    },
    onError: (e) => {
      showSnackbar((e as Error)?.message ?? "Could not create task program");
    },
    onSettled: () => {
      invalidateProgramAndTasks(queryClient, orgId);
    },
  });
};

export const useBulkAssignTaskPrograms = ({ orgId }: { orgId: string }) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (data: BulkAssignTaskProgramsData) => {
      const res = await bulkAssignTaskPrograms(orgId, data);
      return res.data;
    },
    onSuccess: (data) => {
      const changedProgramCount =
        data.createdProgramCount + data.updatedProgramCount;
      if (data.createdTaskCount > 0 && data.pendingQuizCount > 0) {
        showSnackbar(
          `Training pool updated: ${data.createdTaskCount} training task${data.createdTaskCount === 1 ? "" : "s"} sent; ${data.pendingQuizCount} quiz${data.pendingQuizCount === 1 ? " is" : "zes are"} awaiting publication`
        );
      } else if (data.createdTaskCount > 0) {
        showSnackbar(
          `Training pool updated and ${data.createdTaskCount} training task${data.createdTaskCount === 1 ? "" : "s"} sent`
        );
      } else if (data.pendingQuizCount > 0) {
        showSnackbar(
          `Training pool updated; ${data.pendingQuizCount} quiz${data.pendingQuizCount === 1 ? " is" : "zes are"} awaiting publication`
        );
      } else if (changedProgramCount === 0) {
        showSnackbar(
          "No training changes were needed and no trainings were sent"
        );
      } else {
        showSnackbar("Training pool updated");
      }
    },
    onError: (e) => {
      showSnackbar(
        (e as Error)?.message ?? "Could not update the training pool"
      );
    },
    onSettled: () => {
      invalidateProgramAndTasks(queryClient, orgId);
    },
  });
};

export const useUpdateTaskProgram = ({ orgId }: { orgId: string }) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      programId,
      data,
    }: {
      programId: string;
      data: UpdateTaskProgramData;
    }) => {
      const res = await updateTaskProgram(orgId, programId, data);
      return res.data;
    },
    onError: (e) => {
      showSnackbar((e as Error)?.message ?? "Could not update task program");
    },
    onSettled: (_data, _error, variables) => {
      invalidateProgramAndTasks(queryClient, orgId, variables?.programId);
    },
  });
};

export const useDeleteTaskProgram = ({ orgId }: { orgId: string }) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ programId }: { programId: string }) => {
      await deleteTaskProgram(orgId, programId);
    },
    onError: (e) => {
      showSnackbar((e as Error)?.message ?? "Could not delete task program");
    },
    onSettled: (_data, _error, variables) => {
      invalidateProgramAndTasks(queryClient, orgId, variables?.programId);
    },
  });
};

export const useDeleteTaskProgramOccurrence = ({
  orgId,
}: {
  orgId: string;
}) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      programId,
      occurrenceId,
    }: {
      programId: string;
      occurrenceId: string;
    }) => {
      const res = await deleteTaskProgramOccurrence(
        orgId,
        programId,
        occurrenceId
      );
      return res.data;
    },
    onSuccess: (data) => {
      showSnackbar(
        `Deleted revision trainings: ${data.deletedTaskCount} training task(s) removed`
      );
    },
    onError: (e) => {
      showSnackbar(
        (e as Error)?.message ?? "Could not delete training occurrence"
      );
    },
    onSettled: (_data, _error, variables) => {
      invalidateProgramAndTasks(queryClient, orgId, variables?.programId);
    },
  });
};

export const useAddOccurrenceTrainee = ({ orgId }: { orgId: string }) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      programId,
      occurrenceId,
      data,
    }: {
      programId: string;
      occurrenceId: string;
      data: AddOccurrenceTraineeData;
    }) => {
      const res = await addTaskProgramOccurrenceTrainee(
        orgId,
        programId,
        occurrenceId,
        data
      );
      return res.data;
    },
    onSuccess: (data) => {
      const traineeMsg =
        data.createdTaskCount === 0
          ? "No new trainees were added (already in this occurrence)"
          : `Added ${data.createdTaskCount} trainee${data.createdTaskCount === 1 ? "" : "s"} to the occurrence`;
      const programMsg = data.addedToProgram
        ? " and to the training program"
        : "";
      showSnackbar(`${traineeMsg}${programMsg}`);
    },
    onError: (e) => {
      showSnackbar((e as Error)?.message ?? "Could not add trainee");
    },
    onSettled: (_data, _error, variables) => {
      invalidateProgramAndTasks(queryClient, orgId, variables?.programId);
    },
  });
};

export const useDeleteOccurrenceTask = ({ orgId }: { orgId: string }) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      programId,
      occurrenceId,
      taskStateId,
    }: {
      programId: string;
      occurrenceId: string;
      taskStateId: string;
    }) => {
      await deleteTaskProgramOccurrenceTask(
        orgId,
        programId,
        occurrenceId,
        taskStateId
      );
    },
    onError: (e) => {
      showSnackbar((e as Error)?.message ?? "Could not delete training task");
    },
    onSettled: (_data, _error, variables) => {
      invalidateProgramAndTasks(queryClient, orgId, variables?.programId);
    },
  });
};

export const useTriggerTaskProgramForRevision = ({
  orgId,
}: {
  orgId: string;
}) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      programId,
      documentVersionId,
    }: {
      programId: string;
      documentVersionId: string;
    }) => {
      const res = await triggerTaskProgramForRevision(
        orgId,
        programId,
        documentVersionId
      );
      return res.data;
    },
    onSuccess: (data) => {
      showSnackbar(
        `Triggered: ${data.createdTaskCount} training task(s) created`
      );
    },
    onError: (e) => {
      showSnackbar((e as Error)?.message ?? "Could not trigger task program");
    },
    onSettled: (_data, _error, variables) => {
      invalidateProgramAndTasks(queryClient, orgId, variables?.programId);
    },
  });
};
